import { downloadFile, findIndex } from "@dhx/ts-common/core";
import { IGrid } from "@dhx/ts-grid";
import { getDefaultFormats } from "./helpers/format";
import { getCellIds, isWasmSupported } from "./helpers/main";
import { IDataWithStyles, ISpreadsheet } from "./types";
import { DataPage } from "@xbs/muon";

declare const dhx: any;

export class Exporter {
	// tslint:disable-next-line
	private _spreadsheet: ISpreadsheet;
	private _grid: IGrid;
	private _xlsxWorker: Worker;

	constructor(spreadsheet: ISpreadsheet, grid: IGrid) {
		this._spreadsheet = spreadsheet;
		this._grid = grid;
	}

	xlsx() {
		if (!isWasmSupported()) {
			throw new Error("WebAssembly is not supported by your browser");
		}

		const styles = [{ id: "" }];
		const serializedData = this._spreadsheet.serialize() as IDataWithStyles;
		const formats = getDefaultFormats();
		const data = [];
		const cols = this._grid.config.columns.map(col => ({ width: col.width })).splice(1);

		serializedData.sheets.forEach(sheet => {
			const page = (this._spreadsheet as any)._dataStore.getPage(sheet.name) as DataPage;
			const pageSizes = page.getSize();
			const { merged } = page.getMeta();
			const rowItem = [];
			const cells = [];
			for (let i = 0; i < pageSizes[1]; i++) {
				rowItem.push(null);
			}

			for (let i = 0; i < pageSizes[0]; i++) {
				cells.push([...rowItem]);
			}

			sheet.data.forEach(item => {
				let styleInd = null;
				const { row, col } = getCellIds(item.cell, this._grid);
				const defaultFormat = formats.find(format => format.id === item.format);
				const format = defaultFormat ? defaultFormat.mask : "";
				const css = item.css || "";
				const styleId = format + css;

				styleInd = findIndex(styles, s => s.id === styleId);
				if (styleInd === -1) {
					const propsMap = {
						"text-align": "align",
						"font-size": "fontSize",
						"font-weight": "fontWeight",
						"font-style": "fontStyle",
						"text-decoration": "textDecoration",
						"vertical-align": "verticalAlign",
					};
					const cssStyles = dhx.css.get(css) || {};
					const exportStyles = Object.keys(cssStyles).reduce((newStyles, key) => {
						newStyles[propsMap[key] || key] = cssStyles[key];
						return newStyles;
					}, {}) as any;
					styles.push({ format, ...exportStyles, id: styleId });
					styleInd = styles.length - 1;
				}

				const cellValue = {
					v: item.value ? `${item.value}` : "",
					s: styleInd,
				};

				cells[row][col] = cellValue;
			});
			data.push({
				name: sheet.name,
				cells,
				cols: sheet.cols,
				rows: sheet.rows,
				merged,
			});
		});

		const exportData = { data, styles };

		const path = this._spreadsheet.config.exportModulePath;
		const xlsxWorker = this._getXlsxWorker(path);
		xlsxWorker.postMessage({
			type: "convert",
			data: exportData,
		});
	}

	json() {
		return downloadFile(JSON.stringify(this._spreadsheet.serialize()), "data.json", "text/json");
	}

	private _getXlsxWorker(path: string): Worker {
		if (!this._xlsxWorker) {
			const url = window.URL.createObjectURL(
				new Blob([`importScripts('${path}');`], { type: "text/javascript" })
			);
			this._xlsxWorker = new Worker(url);

			this._xlsxWorker.addEventListener("message", e => {
				if (e.data.type === "ready") {
					const a = document.createElement("a");
					a.href = URL.createObjectURL(e.data.blob);
					a.download = "data.xlsx";
					document.body.appendChild(a);
					a.click();
					document.body.removeChild(a);
				}
			});
		}
		return this._xlsxWorker;
	}
}
